import { MenuOutlined, MoreOutlined } from "@ant-design/icons";
import ProfileMenu from "@components/ProfileMenu";
import type { AdminUser } from "@features/users/api";
import type { ThemeMode } from "@shared/store/uiStore";
import type { MenuProps } from "antd";
import { Button, Flex, Layout, Menu, Typography, theme } from "antd";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { findCurrentSection, getNavSections } from "./navigationConfig";

const { Header } = Layout;

interface HeaderBarProps {
  user?: AdminUser;
  mode: ThemeMode;
  onModeChange: (mode: ThemeMode) => void;
  isMobile?: boolean;
  onOpenMobileNav?: () => void;
}

const collectRouteKeys = (items?: MenuProps["items"]): string[] => {
  if (!items || items.length === 0) {
    return [];
  }

  const keys: string[] = [];
  for (const item of items) {
    if (!item) {
      continue;
    }

    if ("key" in item) {
      const key = String(item.key);
      if (key.startsWith("/")) {
        keys.push(key);
      }
    }

    if ("children" in item && Array.isArray(item.children)) {
      keys.push(...collectRouteKeys(item.children as MenuProps["items"]));
    }
  }

  return [...new Set(keys)];
};

const pickSelectedChildKey = (items: MenuProps["items"], pathname: string): string | undefined => {
  if (!items || items.length === 0) {
    return undefined;
  }

  const keys = collectRouteKeys(items);

  if (keys.includes(pathname)) {
    return pathname;
  }

  let best = "";
  for (const key of keys) {
    if (pathname.startsWith(key) && key.length > best.length) {
      best = key;
    }
  }
  return best || undefined;
};

export function HeaderBar({ user, mode, onModeChange, isMobile, onOpenMobileNav }: HeaderBarProps) {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const location = useLocation();

  const sections = useMemo(() => getNavSections(user), [user]);
  const currentSection = useMemo(
    () => findCurrentSection(location.pathname, sections),
    [location.pathname, sections],
  );
  const sectionChildren = currentSection?.children || [];
  const hasChildren = sectionChildren.length > 0;

  const selectedKeys = useMemo(() => {
    const selected = pickSelectedChildKey(sectionChildren, location.pathname);
    return selected ? [selected] : [];
  }, [location.pathname, sectionChildren]);

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (typeof key === "string") {
      navigate(key);
    }
  };

  if (isMobile) {
    return (
      <Header
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          borderBottom: `1px solid ${token.colorBorder}`,
          gap: 4,
          paddingInline: 8,
          paddingBlock: 4,
          height: "auto",
          background: token.colorBgContainer,
        }}
      >
        <Flex align="center" gap={8} style={{ height: 40, minWidth: 0 }}>
          <Button
            type="text"
            icon={<MenuOutlined />}
            aria-label="Открыть меню"
            onClick={onOpenMobileNav}
            disabled={!onOpenMobileNav}
          />

          <Typography.Text
            ellipsis
            style={{ flex: 1, minWidth: 0, fontWeight: 500 }}
            title={currentSection?.label}
          >
            {currentSection?.label ?? "AC Web"}
          </Typography.Text>

          <ProfileMenu user={user} mode={mode} onModeChange={onModeChange} />
        </Flex>

        {hasChildren && (
          <div style={{ overflowX: "auto", overflowY: "hidden", WebkitOverflowScrolling: "touch" }}>
            <Menu
              mode="horizontal"
              disabledOverflow
              items={sectionChildren}
              selectedKeys={selectedKeys}
              onClick={handleMenuClick}
              style={{
                borderBottom: "none",
                background: "transparent",
                minWidth: "max-content",
              }}
            />
          </div>
        )}
      </Header>
    );
  }

  return (
    <Header
      style={{
        display: "flex",
        alignItems: "center",
        borderBottom: `1px solid ${token.colorBorder}`,
        gap: 16,
        background: token.colorBgContainer,
      }}
    >
      <Flex align="center" gap={16} style={{ flex: 1, minWidth: 0, height: "100%" }}>
        {hasChildren && (
          <Menu
            mode="horizontal"
            items={sectionChildren}
            selectedKeys={selectedKeys}
            onClick={handleMenuClick}
            overflowedIndicator={<MoreOutlined />}
            style={{
              flex: 1,
              minWidth: 0,
              borderBottom: "none",
              background: "transparent",
            }}
          />
        )}
      </Flex>

      <Flex align="center" gap={8} style={{ flexShrink: 0, marginInlineStart: "auto" }}>
        <ProfileMenu user={user} mode={mode} onModeChange={onModeChange} />
      </Flex>
    </Header>
  );
}
